/**
 * Copyright 2025 Highway9 Networks Inc.
 */

import { ServiceInstance } from '~/types/serviceInstance';
import { Edge } from '~/types/Edge';
import { Radio } from '~/types/Radio';
import { PayloadServiceInstanceStatus } from '~/types/summary';

type RadioStatusType = "healthy" | "bad" | "needsAttention" | "unconfigured" | "thirdparty";

export function getRadioStatus(radio: Radio): RadioStatusType {
  const overallRadioHealth = radio.runtimeInfo?.overallHealth;

  if (overallRadioHealth === "GOOD") {
    return "healthy";
  }

  if (overallRadioHealth === "BAD") {
    return "bad";
  }

  if (overallRadioHealth === "PARTIALLY_GOOD") {
    return "needsAttention";
  }

  if (overallRadioHealth === "UNKNOWN") {
    return "unconfigured";
  }

  if (overallRadioHealth === "THIRDPARTY") {
    return "thirdparty";
  }

  return "unconfigured";
}

export interface SummaryCalculationsParams {
  filteredServiceInstances: ServiceInstance[];
  filteredEdges: Edge[];
  filteredRadios: Radio[];
  payloadFilters?: {
    serviceInstanceStatus: PayloadServiceInstanceStatus[];
  };
}

export interface SummaryCalculationsResult {
  uniqueFilteredTenants: string[];
  totalServiceInstances: number;
  pausedServiceInstances: number;
  healthyServiceInstances: number;
  totalDeployedEdges: number;
  totalRadios: number;
  healthyDeployedEdges: number;
  unConfiguredEdges: Edge[];
  healthyRadiosCount: number;
  badRadiosCount: number;
  needsAttentionRadiosCount: number;
  thirdPartyRadiosCount: number;
  unConfiguredRadiosCount: number;
}

/**
 * Calculate tenant-related metrics
 */
export function calculateTenantMetrics(filteredServiceInstances: ServiceInstance[]) {
  const totalTenants = filteredServiceInstances.map((serviceInstance) => {
    return serviceInstance.tenantId;
  });

  const uniqueFilteredTenants = [...new Set(totalTenants)];

  return { uniqueFilteredTenants };
}

/**
 * Calculate service instance metrics
 */
export function calculateServiceInstanceMetrics(
  filteredServiceInstances: ServiceInstance[],
  payloadFilters?: { serviceInstanceStatus: PayloadServiceInstanceStatus[] }
) {
  // totalServiceInstances are the ones that are deployed (not paused)
  const totalServiceInstancesList = filteredServiceInstances
    .filter((serviceInstance) => {
      return serviceInstance?.pauseStatus !== 'PAUSED';
    });

  const totalServiceInstances = totalServiceInstancesList.length;
  const pausedServiceInstances = filteredServiceInstances.filter((serviceInstance) => {
    return serviceInstance?.pauseStatus === 'PAUSED';
  }).length;

  // healthyServiceInstances are the ones that are deployed (not paused) and healthy
  const healthyServiceInstances = totalServiceInstancesList
    .filter((serviceInstance) => {
      return serviceInstance?.runtimeInfo?.monitorStatus?.health === 'GOOD';
    })
    .length;

  return { totalServiceInstances, healthyServiceInstances, pausedServiceInstances };
}

/**
 * Calculate edge metrics
 */
export function calculateEdgeMetrics(filteredEdges: Edge[]) {
  let totalDeployedEdges = 0;
  let healthyDeployedEdges = 0;
  const unConfiguredEdges: Edge[] = [];
  for (const edge of filteredEdges) {
    if (edge?.status === 'DEPLOYED') {
      totalDeployedEdges += 1;
      if (edge?.runtimeInfo?.health === 'GOOD') healthyDeployedEdges += 1;
    } else {
      unConfiguredEdges.push(edge);
    }
  }

  return { totalDeployedEdges, healthyDeployedEdges, unConfiguredEdges };
}
/**
 * Fast single-pass tally for edge counts used in tables/widgets
 */
export interface EdgeCounts {
  total: number;
  deployed: number;
  healthy: number;
  bad: number;
  unconfigured: number;
}

/**
 * Group tallies by serviceInstanceId - Edges (single pass)
 */
export function groupEdgeCounts(edges: Edge[]): Record<string, EdgeCounts> {
  const byId: Record<string, EdgeCounts> = {};
  for (const edge of edges) {
    const sid = (edge?.serviceInstanceId ?? '') as string;
    if (!sid) continue;
    const acc = byId[sid] ?? (byId[sid] = { total: 0, deployed: 0, healthy: 0, bad: 0, unconfigured: 0 });
    acc.total += 1;
    if (edge?.status === 'DEPLOYED') {
      acc.deployed += 1;
      if (edge?.runtimeInfo?.health === 'GOOD') acc.healthy += 1;
      else if (edge?.runtimeInfo?.health === 'BAD') acc.bad += 1;
    } else {
      acc.unconfigured += 1;
    }
  }
  return byId;
}

/**
 * Group tallies by serviceInstanceId - Radios (single pass)
 */
export function groupRadioCounts(radios: Radio[]): Record<string, RadioCounts> {
  const byId: Record<string, RadioCounts> = {};
  for (const radio of radios) {
    const sid = (radio?.serviceInstanceId ?? '') as string;
    if (!sid) continue;
    const acc = byId[sid] ?? (byId[sid] = {
      total: 0, deployed: 0, healthy: 0, needsAttention: 0, bad: 0, unconfigured: 0, thirdParty: 0
    });
    acc.total += 1;
    if (radio?.status === 'DEPLOYED') acc.deployed += 1;
    if (radio?.runtimeInfo?.overallHealth === 'GOOD') acc.healthy += 1;
    const status = getRadioStatus(radio as Radio);
    if (status === 'needsAttention') acc.needsAttention += 1;
    else if (status === 'bad') acc.bad += 1;
    else if (status === 'unconfigured') acc.unconfigured += 1;
    else if (status === 'thirdparty') acc.thirdParty += 1;
  }
  return byId;
}

/**
 * Calculate radio metrics
 */
export function calculateRadioMetrics(filteredRadios: Radio[]) {
  // Single pass via tallyRadioCounts - five allocating filter passes before
  const counts = tallyRadioCounts(filteredRadios);
  return {
    healthyRadiosCount: counts.healthy,
    unConfiguredRadiosCount: counts.unconfigured,
    badRadiosCount: counts.bad,
    needsAttentionRadiosCount: counts.needsAttention,
    thirdPartyRadiosCount: counts.thirdParty,
    totalRadios: counts.total,
  };
}

/**
 * Fast single-pass tally for radio counts used in tables/widgets
 */
export interface RadioCounts {
  total: number;
  deployed: number;
  healthy: number;
  needsAttention: number;
  bad: number;
  unconfigured: number;
  thirdParty: number;
}

export function tallyRadioCounts(radios: Radio[]): RadioCounts {
  return radios.reduce<RadioCounts>((acc, radio) => {
    acc.total += 1;
    if (radio?.status === 'DEPLOYED') acc.deployed += 1;
    const overall = radio?.runtimeInfo?.overallHealth;
    if (overall === 'GOOD') {
      acc.healthy += 1;
    }
    const status = getRadioStatus(radio);
    if (status === 'needsAttention') acc.needsAttention += 1;
    else if (status === 'bad') acc.bad += 1;
    else if (status === 'unconfigured') acc.unconfigured += 1;
    else if (status === 'thirdparty') acc.thirdParty += 1;
    return acc;
  }, {
    total: 0,
    deployed: 0,
    healthy: 0,
    needsAttention: 0,
    bad: 0,
    unconfigured: 0,
    thirdParty: 0,
  });
}

/**
 * Calculate all summary metrics at once
 */
export function calculateSummaryMetrics({
  filteredServiceInstances,
  filteredEdges,
  filteredRadios,
  payloadFilters
}: SummaryCalculationsParams): SummaryCalculationsResult {
  const { uniqueFilteredTenants } = calculateTenantMetrics(filteredServiceInstances);
  const { totalServiceInstances, healthyServiceInstances, pausedServiceInstances } = calculateServiceInstanceMetrics(
    filteredServiceInstances,
    payloadFilters
  );
  const { totalDeployedEdges, healthyDeployedEdges, unConfiguredEdges } = calculateEdgeMetrics(filteredEdges);
  const { healthyRadiosCount, unConfiguredRadiosCount, badRadiosCount, needsAttentionRadiosCount, thirdPartyRadiosCount, totalRadios } = calculateRadioMetrics(filteredRadios);

  return {
    uniqueFilteredTenants,
    totalServiceInstances,
    healthyServiceInstances,
    totalDeployedEdges,
    healthyDeployedEdges,
    unConfiguredEdges,
    healthyRadiosCount,
    unConfiguredRadiosCount,
    badRadiosCount,
    needsAttentionRadiosCount,
    thirdPartyRadiosCount,
    pausedServiceInstances,
    totalRadios,
  };
}
